import { AlertTriangle, Check, Loader2, Lock } from 'lucide-react';
import Modal from './Modal';

interface PublishConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Lowercase noun for the thing being published, e.g. 'course'. */
  entityLabel: string;
  /** Title of the item. Long titles are clamped rather than flooding the modal. */
  itemName?: string;
  /** What the user loses access to once published, one short phrase per item. */
  lockedActions: string[];
  isPending?: boolean;
}

/**
 * Shared confirmation for the one-way publish action used by courses, learning
 * paths and certifications. Publishing can't be reversed, so the consequences are
 * broken out into a scannable list instead of one dense paragraph.
 */
export default function PublishConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  entityLabel,
  itemName,
  lockedActions,
  isPending = false,
}: PublishConfirmModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      // Publishing runs server-side validation, so it is not instant — hold the modal
      // open until it settles rather than letting it be dismissed mid-flight.
      onClose={isPending ? () => {} : onClose}
      closeOnOverlayClick={!isPending}
      title={`Publish this ${entityLabel}?`}
      size="md"
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {isPending ? 'Publishing...' : 'Yes, publish'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {itemName && (
          <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
              {entityLabel}
            </p>
            <p className="text-sm font-semibold text-gray-900 line-clamp-3 break-words">{itemName}</p>
          </div>
        )}

        <div className="flex items-start gap-2.5 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-semibold text-amber-900">This action cannot be undone.</p>
        </div>

        <div>
          <p className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-2">
            <Lock className="w-4 h-4 text-gray-500 flex-shrink-0" />
            Once published, you can no longer:
          </p>
          <ul className="space-y-1.5 pl-1">
            {lockedActions.map(action => (
              <li key={action} className="flex items-start gap-2 text-sm text-gray-600">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                <span>{action}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Modal>
  );
}
